// app/_layout.tsx

import { useEffect, useState } from 'react';
import { View, Text, Platform, I18nManager, StyleSheet, Animated } from 'react-native';
import { Stack } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import { useFonts } from 'expo-font';
import {
  NotoSansHebrew_400Regular,
  NotoSansHebrew_500Medium,
  NotoSansHebrew_600SemiBold,
  NotoSansHebrew_700Bold,
  NotoSansHebrew_800ExtraBold,
} from '@expo-google-fonts/noto-sans-hebrew';
import * as SplashScreen from 'expo-splash-screen';
import { useFrameworkReady } from '@/hooks/useFrameworkReady';
import { AccessibilityProvider } from '@/contexts/AccessibilityContext';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { CookieConsent } from '@/components/CookieConsent';
import { SkipToContent } from '@/components/SkipToContent';
import { enableProductionOptimizations } from '@/utils/performanceOptimizations';
import { reportWebVitals, logWebVitals } from '@/utils/webVitals';
import { trackSiteVisit } from '@/utils/analytics';
import { OrganizationSchema } from '@/components/seo/OrganizationSchema';

SplashScreen.preventAutoHideAsync();

const IS_WEB = Platform.OS === 'web';
const PROD_WEB_HOSTS = new Set(['kursiaii.com', 'www.kursiaii.com']);

function isProdWebHost(): boolean {
  if (!IS_WEB) return false;
  if (typeof window === 'undefined') return false;
  const host = window.location?.hostname || '';
  return PROD_WEB_HOSTS.has(host);
}

// ─── Offline banner ───────────────────────────────────────────────────────────
function OfflineBanner({ visible }: { visible: boolean }) {
  const translateY = useState(() => new Animated.Value(-50))[0];

  useEffect(() => {
    Animated.timing(translateY, {
      toValue: visible ? 0 : -50,
      duration: 300,
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, [visible, translateY]);

  if (Platform.OS !== 'web' && !visible) return null;

  return (
    <Animated.View
      style={[styles.offlineBanner, { transform: [{ translateY }] }, { pointerEvents: 'none' }]}
      accessibilityLiveRegion="assertive"
      accessibilityRole="alert"
    >
      <Text style={styles.offlineText}>⚠️ אין חיבור לאינטרנט</Text>
    </Animated.View>
  );
}

export default function RootLayout() {
  useFrameworkReady();

  const isWeb = Platform.OS === 'web';
  const [isOffline, setIsOffline] = useState(false);

  const [fontsLoaded, fontError] = useFonts({
    'NotoSansHebrew-Regular':   NotoSansHebrew_400Regular,
    'NotoSansHebrew-Medium':    NotoSansHebrew_500Medium,
    'NotoSansHebrew-SemiBold':  NotoSansHebrew_600SemiBold,
    'NotoSansHebrew-Bold':      NotoSansHebrew_700Bold,
    'NotoSansHebrew-ExtraBold': NotoSansHebrew_800ExtraBold,
  });

  const appReady = fontsLoaded || !!fontError;

  // Offline detection
  useEffect(() => {
    if (!isWeb || typeof window === 'undefined') return;
    if (typeof navigator !== 'undefined' && 'onLine' in navigator) {
      setIsOffline(!navigator.onLine);
    }
    const handleOffline = () => setIsOffline(true);
    const handleOnline  = () => setIsOffline(false);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online',  handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online',  handleOnline);
    };
  }, [isWeb]);

  // Analytics bootstrap — production only, deferred after page load + idle
  useEffect(() => {
    if (!isWeb) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const w = window as any;
    const d = document;
    const IS_PROD_HOST = isProdWebHost();

    if (!IS_PROD_HOST) {
      w.__KURSI_DISABLE_ANALYTICS__ = true;
      w.__KURSI_ANALYTICS_BOOT__ = 'disabled-non-prod';
      return;
    }

    w.__KURSI_DISABLE_ANALYTICS__ = false;
    w.__KURSI_ANALYTICS_BOOT__ = 'boot-v3-deferred';

    const ensureScript = (id: string, src: string) => {
      if (d.getElementById(id)) return;
      const s = d.createElement('script');
      s.id = id;
      s.async = true;
      s.src = src;
      d.head.appendChild(s);
    };

    function bootAnalytics() {
      if (w.__KURSI_ANALYTICS_LOADED__) return;
      w.__KURSI_ANALYTICS_LOADED__ = true;

      // Google Tag Manager
      w.dataLayer = w.dataLayer || [];
      w.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
      ensureScript('gtm-js', 'https://www.googletagmanager.com/gtm.js?id=GTM-58C4KBGW');

      // Google Analytics
      w.dataLayer = w.dataLayer || [];
      w.gtag = w.gtag || function () { w.dataLayer.push(arguments); };
      ensureScript('ga-gtag-js', 'https://www.googletagmanager.com/gtag/js?id=G-81EDLQB0C9');
      w.gtag('js', new Date());
      w.gtag('config', 'G-81EDLQB0C9', { send_page_view: false });

      // Meta Pixel
      if (!w.fbq) {
        const fbq: any = function () {
          fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments);
        };
        fbq.queue   = [];
        fbq.version = '2.0';
        fbq.loaded  = true;
        fbq.push    = fbq;
        w.fbq  = fbq;
        w._fbq = fbq;
        fbq('init',  '772541885239500');
        fbq('track', 'PageView');
        ensureScript('meta-fbevents', 'https://connect.facebook.net/en_US/fbevents.js');

        if (d.body && !d.getElementById('meta-noscript')) {
          const noscript = d.createElement('noscript');
          noscript.id = 'meta-noscript';
          const img = d.createElement('img');
          img.height = 1;
          img.width  = 1;
          (img.style as any).display = 'none';
          img.src = 'https://www.facebook.com/tr?id=772541885239500&ev=PageView&noscript=1';
          noscript.appendChild(img);
          d.body.appendChild(noscript);
        }
      }
    }

    function scheduleAnalytics() {
      const ric = (w as any).requestIdleCallback;
      if (typeof ric === 'function') {
        ric(bootAnalytics, { timeout: 5000 });
      } else {
        setTimeout(bootAnalytics, 3500);
      }
    }

    if (d.readyState === 'complete') {
      scheduleAnalytics();
    } else {
      w.addEventListener('load', scheduleAnalytics, { once: true });
    }
  }, [isWeb]);

  // RTL baseline
  useEffect(() => {
    if (isWeb && typeof document !== 'undefined') {
      document.documentElement.setAttribute('dir',  'rtl');
      document.documentElement.setAttribute('lang', 'he');
      if (document.body) {
        (document.body.style as any).direction = 'rtl';
        (document.body.style as any).textAlign = 'right';
      }
      return;
    }
    try { I18nManager.allowRTL(true); } catch {}
  }, [isWeb]);

  useEffect(() => {
    if (isWeb) return;
    if (fontsLoaded || fontError) SplashScreen.hideAsync();
  }, [fontsLoaded, fontError, isWeb]);

  useEffect(() => {
    if (!isWeb || typeof window === 'undefined') return;
    if (!isProdWebHost()) return;
    const run = () => {
      try { enableProductionOptimizations(); } catch {}
      try { reportWebVitals(logWebVitals);   } catch {}
      try { trackSiteVisit(window.location.pathname); } catch {}
    };
    const ric = (window as any).requestIdleCallback;
    if (typeof ric === 'function') {
      ric(run, { timeout: 2000 });
    } else {
      setTimeout(run, 0);
    }
  }, [isWeb]);

  if (!appReady && Platform.OS !== 'web') return null;

  return (
    <ErrorBoundary>
      <AccessibilityProvider>
        <OrganizationSchema />
        <SkipToContent />
        <OfflineBanner visible={isOffline} />

        <View
          style={{
            flex: 1,
            position: 'relative',
            backgroundColor: '#F8F7FC',
          }}
          {...(Platform.OS === 'web' && ({ nativeID: 'main-content', tabIndex: -1 } as any))}
        >
          <Stack screenOptions={{ headerShown: false }}>
            <Stack.Screen name="index" options={{ title: 'Kursi | פלטפורמת הקורסים המובילה' }} />
            <Stack.Screen name="chat"  options={{ title: "צ'אט | Kursi" }} />

            {/* ─── Quiz ────────────────────────────────────────────────── */}
            <Stack.Screen name="quiz/index"   options={{ title: 'שאלון המקצוע | Kursi',  gestureEnabled: false }} />
            <Stack.Screen name="quiz/results" options={{ title: 'תוצאות השאלון | Kursi', gestureEnabled: false }} />

            <Stack.Screen name="catalog"           options={{ title: 'קטלוג קורסים | Kursi' }} />
            <Stack.Screen name="course/[courseId]"  options={{ title: 'קורס | Kursi', headerShown: false }} />
            <Stack.Screen name="mentor-signup"     options={{ title: 'הרשמה למנטורים | Kursi' }} />
            <Stack.Screen name="legal/terms"       options={{ title: 'תנאי שימוש | Kursi' }} />
            <Stack.Screen name="legal/privacy"     options={{ title: 'מדיניות פרטיות | Kursi' }} />
            <Stack.Screen name="legal/disclaimer"  options={{ title: 'הגבלת אחריות | Kursi' }} />
            <Stack.Screen name="faq"               options={{ title: 'שאלות נפוצות | Kursi' }} />
            <Stack.Screen name="thank-you"         options={{ title: 'תודה | Kursi' }} />

            {/* ─── Unified learning practice stack ─────────────────────── */}
            <Stack.Screen
              name="learn"
              options={{ title: 'KURSI LEARN', headerShown: false }}
            />


            <Stack.Screen name="+not-found" />
          </Stack>
        </View>

        <StatusBar style="auto" />
        <CookieConsent />
      </AccessibilityProvider>
    </ErrorBoundary>
  );
}

const styles = StyleSheet.create({
  offlineBanner: {
    position: 'absolute',
    top: 0, left: 0, right: 0,
    zIndex: 9999,
    backgroundColor: '#EF4444',
    paddingVertical: 10,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      web:     { boxShadow: '0 2px 8px rgba(0,0,0,0.3)' } as any,
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.3,
        shadowRadius: 4,
        elevation: 10,
      },
    }),
  },
  offlineText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontFamily: 'NotoSansHebrew-Bold',
    textAlign: 'center',
  },
});
